/* import __COLOCATED_TEMPLATE__ from './end-user-auth.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { RESPONSE_PROVIDED_ERRORS } from 'embercom/objects/standalone/constants';
import type IntlService from 'ember-intl/services/intl';
import type SunshineConfiguration from 'embercom/models/standalone/sunshine-configuration';
import type ZendeskConfiguration from 'embercom/models/standalone/zendesk-configuration';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';

export interface Args {
  stackVertically?: boolean;
  hideBorder?: boolean;
  hideTitle?: boolean;
  closeOnSave?: () => void;
}

export default class EndUserAuth extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare intl: IntlService;
  @service declare notificationsService: {
    notifyConfirmation: (message: string) => void;
    notifyResponseError: (
      error: Error,
      messages: { default: string },
      options: { responseProvidedErrors: number[] },
    ) => void;
  };

  @tracked isRevokingAccess = false;
  @tracked isSaving = false;

  get canSave() {
    return (
      this.sunshineConfig &&
      this.sunshineConfig.endUserAuthSigningKeyId &&
      this.sunshineConfig.endUserAuthSigningSecret &&
      this.sunshineConfig.hasDirtyAttributes
    );
  }

  get isDisconnected() {
    return !this.sunshineConfig || !this.sunshineConfig.endUserAuthEnabled;
  }

  get zendeskPlatform() {
    return this.finStandaloneService.zendeskPlatform;
  }

  @action async save() {
    this.isSaving = true;
    try {
      await this.zendeskPlatform.saveZendeskConfig();
      this.notificationsService.notifyConfirmation(
        this.intl.t('standalone.zendesk.settings.connect.save-success'),
      );
      this.args.closeOnSave?.();
    } finally {
      await this.finStandaloneService.zendeskConfig!.reload();
      this.isSaving = false;
    }
  }

  @action async revokeAccess() {
    let isConfirmed = await this.intercomConfirmService.confirm({
      body: this.intl.t('standalone.zendesk.settings.end-user-auth.confirm-body'),
      confirmButtonText: this.intl.t('standalone.zendesk.settings.end-user-auth.confirm-button'),
    });

    try {
      if (isConfirmed) {
        this.isRevokingAccess = true;
        await this.zendeskConfig.revokeEndUserAuth();
        this.notificationsService.notifyConfirmation(
          this.intl.t('standalone.zendesk.settings.end-user-auth.revoke-success-notification'),
        );
      }
    } catch (e) {
      console.error(e.message || e.jqXHR);
      this.notificationsService.notifyResponseError(
        e,
        {
          default: this.intl.t(
            'standalone.zendesk.settings.end-user-auth.revoke-failure-notification',
          ),
        },
        {
          responseProvidedErrors: RESPONSE_PROVIDED_ERRORS,
        },
      );
    } finally {
      this.isRevokingAccess = false;
    }
  }

  get sunshineConfig(): SunshineConfiguration {
    return this.zendeskPlatform.zendeskConfig!.sunshineIntegration!;
  }

  get zendeskConfig(): ZendeskConfiguration {
    return this.zendeskPlatform.zendeskConfig!;
  }

  get removeButtonLabel() {
    return this.isRevokingAccess
      ? this.intl.t('standalone.zendesk.settings.end-user-auth.revoking')
      : this.intl.t('standalone.zendesk.settings.end-user-auth.revoke');
  }

  get connectButtonLabel() {
    return this.isSaving
      ? this.intl.t('standalone.zendesk.settings.end-user-auth.connecting')
      : this.intl.t('standalone.zendesk.settings.end-user-auth.connect');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Zendesk::Settings::EndUserAuth': typeof EndUserAuth;
    'standalone/zendesk/settings/end-user-auth': typeof EndUserAuth;
  }
}
